import { useEffect, useRef, useState } from 'react';
import { apiService } from '../services/api';

const speechLanguages = { en: 'en-US', ru: 'ru-RU', uz: 'uz-UZ' };
const MAX_CLIP_BYTES = 900 * 1024;

export default function useVoiceInput({ value, onChange, language, enabled = true }) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [interim, setInterim] = useState('');
  const current = useRef({ value, onChange });
  current.current = { value, onChange };
  const session = useRef(null);
  const useRecording = useRef(false);

  const cancel = () => {
    const active = session.current;
    session.current = null;
    if (active) {
      clearTimeout(active.timer);
      active.controller?.abort();
      active.recognition?.abort();
      if (active.recorder?.state === 'recording') active.recorder.stop();
      active.stream?.getTracks().forEach(track => track.stop());
    }
    setStatus('idle');
    setInterim('');
  };

  useEffect(() => {
    // Invalidate callbacks before stopping a session on close, language change or unmount.
    setStatus('idle');
    setInterim('');
    setError('');
    return () => {
      const active = session.current;
      session.current = null;
      if (!active) return;
      clearTimeout(active.timer);
      active.controller?.abort();
      active.recognition?.abort();
      if (active.recorder?.state === 'recording') active.recorder.stop();
      active.stream?.getTracks().forEach(track => track.stop());
    };
  }, [language, enabled]);

  const append = (text) => {
    if (!text.trim()) return;
    const previous = current.current.value || '';
    const next = `${previous}${previous && !/\s$/.test(previous) ? ' ' : ''}${text.trim()} `;
    current.current.value = next;
    current.current.onChange(next);
  };

  const toggle = async () => {
    if (!enabled) return;
    const previous = session.current;
    if (previous) {
      if (previous.recorder?.state === 'recording') previous.recorder.stop();
      else if (previous.recognition) previous.recognition.stop();
      else cancel();
      return;
    }
    setError('');
    setInterim('');
    if (window.isSecureContext === false) { setError('speechSecure'); return; }
    const active = {};
    session.current = active;
    const isCurrent = () => session.current === active;
    const finish = (message = '') => {
      if (!isCurrent()) return;
      clearTimeout(active.timer);
      active.stream?.getTracks().forEach(track => track.stop());
      session.current = null;
      setStatus('idle');
      setInterim('');
      if (message) setError(message);
    };
    setStatus('starting');
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (Recognition && !useRecording.current) {
      try {
        const recognition = new Recognition();
        active.recognition = recognition;
        recognition.lang = speechLanguages[language] || speechLanguages.en;
        recognition.continuous = false;
        recognition.interimResults = true;
        const committed = new Set();
        recognition.onstart = () => { if (isCurrent()) setStatus('listening'); };
        recognition.onresult = event => {
          if (!isCurrent()) return;
          const final = [];
          const pending = [];
          for (let i = 0; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
              if (!committed.has(i)) { final.push(event.results[i][0].transcript); committed.add(i); }
            } else pending.push(event.results[i][0].transcript);
          }
          append(final.join(' '));
          setInterim(pending.join(' '));
        };
        recognition.onerror = event => {
          if (!isCurrent()) return;
          const fallback = ['network', 'service-not-allowed', 'language-not-supported'].includes(event.error);
          if (fallback) useRecording.current = true;
          finish(fallback ? 'speechFallback' : ({ 'not-allowed': 'speechPermission', 'audio-capture': 'speechMicrophone', 'no-speech': 'speechNoSpeech', aborted: '' }[event.error] || 'speechFailed'));
        };
        recognition.onend = () => finish(committed.size ? '' : 'speechNoSpeech');
        recognition.start();
        active.timer = setTimeout(() => { if (isCurrent()) recognition.stop(); }, 30000);
      } catch {
        useRecording.current = true;
        finish('speechFallback');
      }
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      finish('speechUnsupported');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!isCurrent()) { stream.getTracks().forEach(track => track.stop()); return; }
      active.stream = stream;
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'].find(type => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 64000 });
      active.recorder = recorder;
      const chunks = [];
      let bytes = 0;
      recorder.ondataavailable = event => {
        if (!isCurrent() || !event.data.size) return;
        chunks.push(event.data);
        bytes += event.data.size;
        if (bytes > MAX_CLIP_BYTES && recorder.state === 'recording') recorder.stop();
      };
      recorder.onerror = () => {
        finish('speechMicrophone');
        if (recorder.state === 'recording') recorder.stop();
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        if (!isCurrent()) return;
        clearTimeout(active.timer);
        if (!bytes) { finish('speechNoSpeech'); return; }
        if (bytes > MAX_CLIP_BYTES) { finish('speechTooLong'); return; }
        setStatus('processing');
        active.controller = new AbortController();
        active.timer = setTimeout(() => active.controller.abort(), 50000);
        try {
          const text = await apiService.transcribeAudio(new Blob(chunks, { type: recorder.mimeType || mimeType }), language, active.controller.signal);
          if (!isCurrent()) return;
          append(text);
          finish(text.trim() ? '' : 'speechNoSpeech');
        } catch (err) {
          finish(({ no_speech: 'speechNoSpeech', speech_limit: 'speechLimit', speech_too_large: 'speechTooLong', speech_auth: 'speechAuth' })[err.message] || 'speechFailed');
        }
      };
      recorder.start(1000);
      setStatus('recording');
      active.timer = setTimeout(() => { if (isCurrent() && recorder.state === 'recording') recorder.stop(); }, 30000);
    } catch (err) {
      finish(err.name === 'NotAllowedError' ? 'speechPermission' : 'speechMicrophone');
    }
  };

  return { status, error, interim, toggle, cancel, isListening: ['starting', 'listening', 'recording'].includes(status), isProcessing: status === 'processing' };
}
